'use client'
import React from 'react'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import DescriptionSection from './DescriptionSection'
import Image from 'next/image'
import { RiMapPinLine, RiStarFill } from 'react-icons/ri'
import { Box, Flex, Skeleton, Text } from '@mantine/core'

const SkeltonRating = () => {
  return (
    <div className='gap-4 mx-4'>
      {/* Star + rating skeleton */}
      <Skeleton width={81} height={38} radius='xl' className='mb-2' />

      {/* Reviews count skeleton */}
      <Skeleton
        width={70}
        height={16}
        radius='sm'
        className='mx-2 mt-4'
        style={{
          display: 'inline-block',
          verticalAlign: 'middle',
        }}
        mx={5}
        mt={4}
      />
    </div>
  )
}

const DesktopPropertyDetailsSummaryContent = ({
  loading,
  data,
  words,
  isLong,
  propertyDetails,
}) => {
  const router = useRouter()
  const t = useTranslations('propertydetails')
  const reviewsCount = Number(data?.data?.reviews_count ?? 0)
  const hasReviews = reviewsCount > 0

  const handleCityClick = () => {
    const city = data?.data?.city
    if (city) router.push(`/property-listing?location=${encodeURIComponent(city)}`)
  }

  return (
    <section className='max-md:hidden'>
      <div className='w-full'>
        {/* Summary */}
        <div className='text-[#000D33] flex flex-col gap-5'>
          <div className='w-full'>
            <div className='flex items-center  gap-4 xl:justify-between'>
              <div className='flex flex-col '>
                <h1 className='text-[24px] max-lg:text-[22px] font-semibold pb-2'>
                  {loading || !data ? (
                    <Skeleton w={300} h={32} />
                  ) : (
                    data?.data?.name
                  )}
                </h1>
                {loading || !data ? (
                  <Skeleton w={600} h={22} />
                ) : (
                  <Flex w={600} h={22} gap={14} align='center'>
                    {propertyDetails?.map((item, index) => (
                      <React.Fragment key={`${item.label}-${index}`}>
                        <Flex h={21} gap={9} align='center'>
                          <Image
                            src={item.icon}
                            alt={item.label}
                            width={item.imgWidth}
                            height={item.imgHeight}
                          />
                          <Text
                            c='#191919'
                            fw={500}
                            fz={13}
                            lh='160%'
                            style={{ letterSpacing: 'normal' }}
                          >
                            {item.value} {item.label}
                          </Text>
                        </Flex>

                        {/* Add separator dot except after last item */}
                        {index !== propertyDetails.length - 1 && (
                          <Box
                            w={5}
                            h={5}
                            bg='#CECECE'
                            style={{
                              borderRadius: '50%',
                              display: 'inline-block',
                            }}
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </Flex>
                )}
              </div>

              {loading || !data ? (
                <SkeltonRating />
              ) : (
                <div className='gap-4 '>
                  {hasReviews ? (
                    <>
                      <div className='flex items-center justify-center gap-[7px] w-[81px] h-[38px] px-[12px] py-2 rounded-[34px] bg-[#191919] opacity-100'>
                        <RiStarFill className='w-[12.29px] h-[12.75px] text-[#FFA125]' />
                        <span className='font-semibold text-white text-[14px]'>
                          {Number(data?.data?.overall_reviews ?? 0).toFixed(1)}
                        </span>
                      </div>

                      <div className='font-medium text-[13px] leading-[160%] tracking-[0] text-[#898989] mx-2 mt-4'>
                        {reviewsCount} {reviewsCount === 1 ? 'review' : 'reviews'}
                      </div>
                    </>
                  ) : (
                    <div className='flex items-center justify-center gap-[7px] w-fit h-[38px] px-[12px] py-2 rounded-[34px] bg-[#F6F6F6] opacity-100'>
                      <RiStarFill className='w-[12.29px] h-[12.75px] text-[#FFA125]' />
                      <span className='font-semibold text-[#191919] text-[14px]'>
                        {t('badges.new')}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className='w-full opacity-100 my-2'>
              {loading || !data ? (
                <Skeleton w={500} h={12} />
              ) : (
                <p className='text-[#191919] font-[Poppins] font-normal text-[13px] leading-[160%] tracking-normal'>
                  {data?.data?.short_summary}
                </p>
              )}
            </div>

            {loading || !data ? (
              <div className='w-[167px] h-[21px] flex items-center gap-[4px] opacity-100 my-2'>
                <Skeleton w={16} h={16} />
                <Skeleton w={120} h={16} />
              </div>
            ) : (
              <div className='w-full h-[21px] flex items-center gap-[4px] opacity-100 my-2 md:mt-[20px]'>
                <RiMapPinLine className='text-[#80c7e2] w-4 h-4' />
                <button
                  type='button'
                  onClick={handleCityClick}
                  className='text-[#5F6369] font-medium text-[13px] leading-[160%] tracking-normal hover:underline underline-offset-2 cursor-pointer'
                >
                  {data?.data?.city}, {data?.data?.country}
                </button>
              </div>
            )}
            {/* --------------------------Need to be uncommented---------------------------------- */}
            {/* <QuickFeautersDesktop
                        loadingQuickFeature={loading}
                        data={data?.data?.property_amenities}
                      /> */}
            {/* ------------------------------------------------------------------------- */}
            <DescriptionSection
              loading={loading}
              data={data}
              isLong={isLong}
              words={words}
            />
          </div>
        </div>
      </div>
    </section>
  )
}

export default DesktopPropertyDetailsSummaryContent
